import { FC } from "react";
import { Box, Button, CircularProgress, IconButton, Tooltip, Typography, useTheme } from "@mui/material";
import { ChevronLeftRounded, ContentCopyRounded, DescriptionOutlined, VisibilityOutlined } from "@mui/icons-material";
import { format } from "date-fns";
import { PatientHexAvatar } from "../../../../components/ui";
import { Details } from "../../query/topic/products/product-detail";

export type SummaryActive = {
    name: string;
    /** Função/mecanismo resumido do ativo */
    fn: string;
    /** Concentração já formatada (ex.: "3%", "2,5 g") */
    conc: string;
    /** Detalhe do ativo para o drawer de "Informações" (#1) */
    detail?: Details;
};

type Props = {
    /** Título do resumo (nome do paciente; cai no foco da consulta em deep-link) */
    title: string;
    focus?: string;
    /** Data ISO da consulta (opcional) */
    date?: string;
    /** Rótulo do tipo: "Tópico" | "Oral" */
    typeLabel: string;
    vehicle?: string | null;
    vehicleObservation?: string;
    period?: string | null;
    actives: SummaryActive[];
    observations: string[];
    loading: boolean;
    onBack: () => void;
    onDuplicate: () => void;
    onRecipe: () => void;
    /** Abre o detalhe padrão do ativo (#1). Sem ele, a linha não fica clicável. */
    onViewActive?: (active: SummaryActive) => void;
};

const ConsultationSummary: FC<Props> = ({
    title,
    focus,
    date,
    typeLabel,
    vehicle,
    vehicleObservation,
    period,
    actives,
    observations,
    loading,
    onBack,
    onDuplicate,
    onRecipe,
    onViewActive,
}) => {
    const theme = useTheme();
    const gridCols = onViewActive ? "2fr 1.3fr 0.8fr 40px" : "2fr 1.4fr 0.9fr";

    const meta = [focus, date ? format(new Date(date), "dd/MM/yyyy") : null, typeLabel]
        .filter(Boolean)
        .join(" · ");

    return (
        <Box sx={{ width: "100%", p: { xs: "18px 16px 24px", md: 4 }, maxWidth: 900, mx: "auto" }}>
            {/* Voltar */}
            <Button
                startIcon={<ChevronLeftRounded sx={{ fontSize: 18 }} />}
                onClick={onBack}
                sx={{
                    mb: 2.5,
                    px: 0,
                    minWidth: 0,
                    color: "text.secondary",
                    fontWeight: 600,
                    fontSize: "13.5px",
                    "&:hover": { backgroundColor: "transparent", color: "text.primary" },
                }}
            >
                Voltar para o histórico
            </Button>

            {/* Header */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2.5,
                    flexWrap: "wrap",
                    mb: 3,
                    p: { xs: "18px", md: "26px 28px" },
                    backgroundColor: "background.paper",
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: "18px",
                }}
            >
                <PatientHexAvatar name={title || "?"} size={60} />
                <Box sx={{ flex: 1, minWidth: 200 }}>
                    <Typography
                        sx={{
                            fontFamily: theme.typography.fontFamilyDisplay,
                            fontSize: { xs: "22px", md: "26px" },
                            color: "text.primary",
                            lineHeight: 1.15,
                            mb: 0.5,
                        }}
                    >
                        {title || "Consulta"}
                    </Typography>
                    <Typography sx={{ fontSize: "13px", color: "text.secondary" }}>{meta}</Typography>
                </Box>

                <Box
                    component="span"
                    sx={{
                        fontSize: "12px",
                        fontWeight: 600,
                        px: "13px",
                        py: "5px",
                        borderRadius: "100px",
                        backgroundColor: theme.palette.brand.successBg,
                        color: theme.palette.success.dark,
                    }}
                >
                    Concluída
                </Box>

                <Box sx={{ display: "flex", gap: 1.25, width: { xs: "100%", sm: "auto" } }}>
                    <Button
                        variant="outlined"
                        startIcon={<ContentCopyRounded sx={{ fontSize: 15 }} />}
                        onClick={onDuplicate}
                        sx={{ height: 44, px: 2, fontSize: "13px", fontWeight: 600, borderColor: theme.palette.primary.main, color: "primary.main" }}
                    >
                        Duplicar
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<DescriptionOutlined sx={{ fontSize: 15 }} />}
                        onClick={onRecipe}
                        sx={{ height: 44, px: 2, fontSize: "13px" }}
                    >
                        Baixar receita
                    </Button>
                </Box>
            </Box>

            {loading ? (
                <Box sx={{ p: 6, display: "flex", justifyContent: "center" }}>
                    <CircularProgress size={28} />
                </Box>
            ) : (
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", md: "1.7fr 1fr" },
                        gap: { xs: 2, md: 3 },
                        alignItems: "start",
                    }}
                >
                    {/* Fórmula prescrita */}
                    <Box
                        sx={{
                            backgroundColor: "background.paper",
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: "18px",
                            overflow: "hidden",
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 1.5,
                                px: 3,
                                py: 2.25,
                                borderBottom: `1px solid ${theme.palette.divider}`,
                            }}
                        >
                            <Typography sx={{ fontFamily: theme.typography.fontFamilyDisplay, fontSize: "21px", color: "text.primary" }}>
                                Fórmula prescrita
                            </Typography>
                            {vehicle && (
                                <Box
                                    component="span"
                                    sx={{
                                        fontSize: "12px",
                                        fontWeight: 600,
                                        px: "11px",
                                        py: "4px",
                                        borderRadius: "100px",
                                        color: theme.palette.primary.dark,
                                        backgroundColor: theme.palette.brand.cream,
                                        border: `1px solid ${theme.palette.brand.heroBorder}`,
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {vehicle}
                                </Box>
                            )}
                        </Box>

                        {/* Cabeçalho da tabela */}
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: gridCols,
                                gap: 1.5,
                                px: 3,
                                py: 1.5,
                                fontSize: "10.5px",
                                letterSpacing: "0.8px",
                                textTransform: "uppercase",
                                fontWeight: 600,
                                color: "text.secondary",
                                borderBottom: `1px solid ${theme.palette.divider}`,
                            }}
                        >
                            <Box>Ativo</Box>
                            <Box>Função</Box>
                            <Box sx={{ textAlign: "right" }}>Conc.</Box>
                            {onViewActive && <Box />}
                        </Box>

                        {actives.length === 0 ? (
                            <Typography sx={{ px: 3, py: 2.5, fontSize: "13.5px", color: "text.secondary", fontStyle: "italic" }}>
                                Nenhum ativo nesta consulta.
                            </Typography>
                        ) : (
                            actives.map((active, idx) => {
                                const canView = !!onViewActive && !!active.detail;
                                return (
                                    <Box
                                        key={`${active.name}-${idx}`}
                                        onClick={canView ? () => onViewActive!(active) : undefined}
                                        sx={{
                                            display: "grid",
                                            gridTemplateColumns: gridCols,
                                            gap: 1.5,
                                            px: 3,
                                            py: 1.75,
                                            alignItems: "center",
                                            borderBottom: `1px solid ${theme.palette.divider}`,
                                            "&:last-of-type": { borderBottom: "none" },
                                            cursor: canView ? "pointer" : "default",
                                            ...(canView && { "&:hover": { backgroundColor: theme.palette.brand.surface2 } }),
                                        }}
                                    >
                                        <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "text.primary" }}>{active.name}</Typography>
                                        <Typography
                                            title={active.fn || undefined}
                                            sx={{
                                                fontSize: "13px",
                                                color: theme.palette.brand.heroText,
                                                display: "-webkit-box",
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: "vertical",
                                                overflow: "hidden",
                                            }}
                                        >
                                            {active.fn || "—"}
                                        </Typography>
                                        <Typography sx={{ fontSize: "13.5px", fontWeight: 600, color: "primary.main", textAlign: "right" }}>
                                            {active.conc}
                                        </Typography>
                                        {onViewActive && (
                                            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                                                {canView && (
                                                    <Tooltip title="Informações" placement="top">
                                                        <IconButton
                                                            size="small"
                                                            aria-label={`Informações de ${active.name}`}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                onViewActive!(active);
                                                            }}
                                                            sx={{ color: "primary.main" }}
                                                        >
                                                            <VisibilityOutlined sx={{ fontSize: 18 }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        )}
                                    </Box>
                                );
                            })
                        )}
                    </Box>

                    {/* Coluna direita */}
                    <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 2, md: 3 } }}>
                        {(period || vehicle || vehicleObservation) && (
                            <Box
                                sx={{
                                    backgroundColor: "background.paper",
                                    border: `1px solid ${theme.palette.divider}`,
                                    borderRadius: "18px",
                                    p: "22px 24px",
                                }}
                            >
                                {period && (
                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "baseline",
                                            pb: vehicle ? 1.75 : 0,
                                            borderBottom: vehicle ? `1px solid ${theme.palette.divider}` : "none",
                                        }}
                                    >
                                        <Typography sx={{ fontSize: "13px", color: "text.secondary" }}>Período</Typography>
                                        <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "text.primary" }}>{period}</Typography>
                                    </Box>
                                )}
                                {vehicle && (
                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "baseline",
                                            pt: period ? 1.75 : 0,
                                            pb: vehicleObservation ? 1.75 : 0,
                                            borderBottom: vehicleObservation ? `1px solid ${theme.palette.divider}` : "none",
                                        }}
                                    >
                                        <Typography sx={{ fontSize: "13px", color: "text.secondary" }}>Veículo</Typography>
                                        <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "text.primary" }}>{vehicle}</Typography>
                                    </Box>
                                )}
                                {vehicleObservation && (
                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "flex-start",
                                            pt: period || vehicle ? 1.75 : 0,
                                            gap: 2,
                                        }}
                                    >
                                        <Typography sx={{ fontSize: "13px", color: "text.secondary", flexShrink: 0 }}>Obs. base</Typography>
                                        <Typography
                                            sx={{
                                                fontSize: "14px",
                                                fontWeight: 600,
                                                color: "text.primary",
                                                textAlign: "right",
                                                lineHeight: 1.45,
                                                overflow: "hidden",
                                                display: "-webkit-box",
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: "vertical",
                                            }}
                                        >
                                            {vehicleObservation}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        )}

                        <Box
                            sx={{
                                backgroundColor: "background.paper",
                                border: `1px solid ${theme.palette.divider}`,
                                borderRadius: "18px",
                                p: "22px 24px",
                            }}
                        >
                            <Typography sx={{ fontFamily: theme.typography.fontFamilyDisplay, fontSize: "19px", color: "text.primary", mb: 1 }}>
                                Observações
                            </Typography>
                            {observations.length === 0 ? (
                                <Typography sx={{ fontSize: "13.5px", color: "text.secondary", fontStyle: "italic" }}>
                                    Sem observações
                                </Typography>
                            ) : (
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                                    {observations.map((obs, idx) => (
                                        <Typography key={idx} sx={{ fontSize: "13.5px", color: "text.primary", lineHeight: 1.65 }}>
                                            {obs}
                                        </Typography>
                                    ))}
                                </Box>
                            )}
                        </Box>
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default ConsultationSummary;